/**
 * Audit trail — the record of who did what, to whose account, while holding
 * whose authority.
 *
 * Every WRITE that leaves the browser is offered to this module by the API
 * layer (see `setWriteObserver` in lib/api.js), so coverage is STRUCTURAL: a
 * new write route is audited the day it is added, without anyone remembering
 * to instrument the call site. Writes performed while acting-as are the ones
 * that matter most and are always recorded; ordinary writes are recorded too,
 * flagged `actingAs: null`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PERSISTENCE IS A STUB. sr-portal-backend has no audit route yet, so events
 * are POSTed to /api/audit and, when that 404s, fall back to a bounded
 * sessionStorage ring buffer that dies with the tab. A client-held audit log
 * is evidence of nothing — the actor can edit or drop it. The real trail must
 * be written server-side, from the verified JWT, on the same request that
 * performs the write. See RBAC.md ("Backend work required").
 * ─────────────────────────────────────────────────────────────────────────
 */

import { api } from './api.js'
import {getSessionGeneration, onSessionGenerationChange} from './sessionGeneration.js'

const STORE_KEY = 'sr_audit_pending'
const MAX_LOCAL = 200

let sinkAvailable = null // null = unknown, true/false once probed
const listeners = new Set()

/* ---- Event shape ---------------------------------------------------- */

let seq = 0

/**
 * Canonical audit event. `before`/`after` are optional but strongly
 * encouraged: a write that records only its intent tells you what was asked
 * for, not what changed.
 */
export function makeAuditEvent({
  actor,
  actorRole,
  actingAs,
  action,
  entity,
  entityId,
  method,
  path,
  before,
  after,
  ok,
  status,
  error,
} = {}) {
  seq += 1
  return {
    id: `${Date.now().toString(36)}-${seq.toString(36)}`,
    at: new Date().toISOString(),
    actor: actor || null, // { id, username, name }
    actorRole: actorRole || null,
    actingAs: actingAs || null, // { id, name } — null for ordinary writes
    action: action || `${method || 'POST'} ${path || ''}`.trim(),
    entity: entity || null,
    entityId: entityId != null ? String(entityId) : null,
    method: method || null,
    path: path || null,
    before: before ?? null,
    after: after ?? null,
    ok: ok !== false,
    status: status ?? null,
    error: error || null,
  }
}

/* ---- Local fallback store ------------------------------------------- */

function readLocal() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeLocal(events) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(events.slice(-MAX_LOCAL)))
  } catch {
    /* storage unavailable — the in-memory listeners still saw the event */
  }
}

/** The locally-held trail, newest first. Debug/AuditPanel use only. */
export function readAuditTrail() {
  return readLocal().slice().reverse()
}

/** How many events never reached a server. Surfaced in the UI as an honest gap. */
export function pendingCount() {
  return readLocal().filter((e) => e.persisted !== true).length
}

/** Is the backend audit sink live? null until the first write probes it. */
export function sinkStatus() {
  return sinkAvailable
}

export function onAudit(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit(event) {
  for (const fn of listeners) {
    try {
      fn(event)
    } catch {
      /* a broken listener must never break the write it is observing */
    }
  }
}

/* ---- The sink ------------------------------------------------------- */

/**
 * Record one event. Never throws and never rejects: an audit failure must not
 * take down the user action it is observing. It returns the stored event so
 * callers can surface "recorded / not persisted".
 */
export async function recordAudit(input, {generation = getSessionGeneration()} = {}) {
  if (generation !== getSessionGeneration()) {
    console.info('[audit] Ignored stale-session local telemetry; not sent in the replacement session.')
    return {persisted: false, outcome: 'stale_session_not_sent'}
  }
  const event = input?.id && input?.at ? input : makeAuditEvent(input)

  // Optimistically hold it locally first, so an in-flight POST that fails
  // still leaves a trace in this tab.
  const local = readLocal()
  local.push({ ...event, persisted: false })
  writeLocal(local)
  emit(event)

  try {
    await api.auditWrite(event)
    if (generation !== getSessionGeneration()) return {...event, persisted: null, outcome: 'session_changed_unknown'}
    sinkAvailable = true
    const list = readLocal()
    const i = list.findIndex((e) => e.id === event.id)
    if (i >= 0) {
      list[i].persisted = true
      writeLocal(list)
    }
    return { ...event, persisted: true }
  } catch (err) {
    if (generation !== getSessionGeneration()) return {...event, persisted: null, outcome: 'session_changed_unknown'}
    // 404 = the route isn't deployed yet (the backend 404s before auth), which
    // is the EXPECTED state today — not an error worth shouting about.
    if (err?.status === 404) {
      sinkAvailable = false
    } else if (import.meta.env?.DEV) {
      console.warn('[audit] sink write failed', err?.message || err)
    }
    return { ...event, persisted: false }
  }
}

/** Drop the local buffer (sign-out). Does not touch anything server-side. */
export function clearLocalAudit() {
  try {
    sessionStorage.removeItem(STORE_KEY)
  } catch {
    /* nothing to clear */
  }
}


let droppedEphemeralEvents = 0
function discardPriorSessionTelemetry() {
  const count = readLocal().length
  clearLocalAudit()
  sinkAvailable = null
  if (count) {
    droppedEphemeralEvents += count
    console.info('[audit] Discarded ' + count + ' prior-session local telemetry items; not resent. Durable server history is unchanged.')
  }
}
export const auditSessionDiagnostics = () => ({droppedEphemeralEvents})
// Legacy browser buffers have no trustworthy session binding. They are not a
// retry queue or durable history, and must never be replayed under a new login.
discardPriorSessionTelemetry()
onSessionGenerationChange(discardPriorSessionTelemetry)
